
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    /* eslint-disable indent */
import { IMedia } from '@/types';
import { ANALYTICS_EVENTS, useSendAnalyticsEvent } from 'api/analytics';
import { artworkPagePopulation, getArtwork, getCustomArtwork, getHasSales, getIsTopViewed, getWasSoldRecently, isArtworkSaved, saveArtwork, unsaveArtwork, } from 'api/artworks';
import { useAvailableAuction } from 'api/auctions';
import { getShippingCost } from 'api/shipping-cost';
import { notifyMeService, upcomingReleaseUserService, } from 'api/upcoming-release';
import classNames from 'classnames';
import ErrorBoundary from 'components/abstract/ErrorBoundary';
import { OnScreenComponent } from 'components/abstract/OnScreenComponent';
import { ArtistsNames } from 'components/artists/ArtistsNames';
import { ArtworkCardProps } from 'components/artwork/ArtworkCard';
import { ArtworkTradeSkeleton } from 'components/artwork/ArtworkPageSkeleton';
import { ArtworkTags } from 'components/artwork/ArtworkTags';
import { ArtworkTrade } from 'components/artwork/ArtworkTrade';
import { ArtworkDetails } from 'components/artwork/ArtworksDetails';
import { ExistingTrades } from 'components/artwork/ExistingTrades';
import { MoreFromArtistCarousel } from 'components/artwork/MoreFromArtistCarousel';
import { PopularityMessage } from 'components/artwork/PopularityMessage';
import { ViewPreviousTrades } from 'components/artwork/ViewPreviousTrades';
import { LooksSimilarCarousel } from 'components/carousels/LooksSimilarCarousel';
import { ReviewsCarouselSkeleton } from 'components/carousels/ReviewsCarouselSkeleton';
import { SearchedArtworksContainer } from 'components/carousels/SearchedArtworksContainer';
import { AvailableAuctionBanner } from 'components/common/AvailableAuctionBanner';
import { Badge } from 'components/common/Badge';
import { Breadcrumbs } from 'components/common/Breadcrumbs';
import { Button } from 'components/common/Button';
import { Icon } from 'components/common/Icon';
import { Popover } from 'components/common/Popover';
import { SaveButton } from 'components/common/SaveButton';
import { Skeletons } from 'components/common/Skeletons';
import { SlideShow } from 'components/common/SlideShow';
import { useWaysTo } from 'components/common/ways-to/WaysTo';
import routes from 'constants/routes';
import { useFeatureFlags } from 'hooks';
import { useAutomaticShowAuthModal } from 'hooks/useAutomaticShowAuthModal';
import { BaseLayout } from 'layouts/BaseLayout';
import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from 'next';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import qs from 'qs';
import { ComponentProps, lazy, ReactElement, Suspense, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { fetchByMedium, fetchFeaturedArtworks, fetchLatestArtworksAsks, fetchLatestArtworksBids, fetchNewReleases, } from 'services/homepage';
import { isNearlyGone as _isNearlyGone } from 'utils/artwork';
import { Artist, Edition, mapIArtistsToArtists, mapIArtworkToEditions, } from 'utils/data';
import { ARTWORK_IMG_PLACEHOLDER, getImageUrl, isMediaImage, Media, TImages, } from 'utils/image';
import { sortArtworkImages } from 'utils/sort';
import { getEditionShortName, titleCase } from 'utils/strings';
import { getArtworkUrl } from 'utils/urls';
import { schemaOrgUrls } from '../../constants';
import { useAuth, useAuthModal, useDrawer, useOverlay } from '../../contexts';
import { useLatestSearches } from '../../hooks';
const SearchedArtworksCarousel = dynamic(() => import('components/carousels/SearchedArtworksCarousel').then(m => m.SearchedArtworksCarousel), { ssr: false });
const SavedArtworkModal = dynamic(() => import('components/artwork/SavedArtworkModal').then(m => m.SavedArtworkModal), { ssr: false });
const PillMessage = dynamic(() => import('components/artwork/PillMessage').then(m => m.PillMessage), { ssr: true });
const IntercomTrigger = dynamic(() => import('components/common/Intercom').then(m => m.IntercomTrigger), { ssr: false });
const ReviewsCarousel = lazy(() => import('components/carousels/ReviewsCarousel').then(m => ({
    default: m.ReviewsCarousel
})));
const VIEWED_ARTWORKS = 'viewed_artworks';
const artworkQuery = qs.stringify({
    ...artworkPagePopulation,
    filters: {
        editions: {
            asks: {
                status: {
                    $eq: 'Approved'
                }
            },
            bids: {
                status: {
                    $eq: 'Approved'
                }
            }
        }
    }
}, { encodeValuesOnly: true });
const popularityTexts: Record<NonNullable<MessageToShow>, string> = {
    'nearly-gone': 'single_artwork:nearly_gone',
    'recently-sold': 'single_artwork:recently_sold',
    trending: 'single_artwork:trending_this_week'
};
type MessageToShow = 'recently-sold' | 'trending' | 'nearly-gone' | null;
type Props = {
    id: number;
    initialData: {
        name: string;
        artists: Artist[];
        images: TImages;
        editions: Edition[];
    };
    initialCurrentEdition: Edition | null;
    initialArtworkDetails: ComponentProps<typeof ArtworkDetails> | null;
    hasSales?: boolean;
    shippingCost?: number | null;
    url?: string;
    wasSoldRecently?: boolean;
    isTopViewed?: boolean;
    isNearlyGone?: boolean;
    messageToShow?: MessageToShow;
};
const Artwork = ({ id, initialData: { artists, editions: initialEditions, images, name }, initialCurrentEdition, initialArtworkDetails, shippingCost, url, hasSales, messageToShow, }: Props) => {
    const { t } = useTranslation();
    const router = useRouter();
    const { user, isAuthenticated } = useAuth();
    const { setIsOpen: setIsOverlayOpen, setConfig: setOverlayConfig } = useOverlay();
    const { setIsOpen: setIsDrawerOpen } = useDrawer();
    const { setIsOpen: setAuthModalIsOpen, setState: setAuthModalState } = useAuthModal();
    const { featureFlags } = useFeatureFlags();
    const { sendAnalyticsEvent } = useSendAnalyticsEvent();
    const setWaysTo = useWaysTo();
    const { availableAuction } = useAvailableAuction();
    const artistHasAuction = Boolean(artists?.find(a => a.id == availableAuction?.artistId));
    const [artworkDetails, setArtworkDetails] = useState<typeof initialArtworkDetails>(initialArtworkDetails);
    const queryClient = useQueryClient();
    const isInitiaRender = useRef(true);
    const currentEditionName = useMemo(() => {
        const editionQuery = router.query.edition;
        if (editionQuery && typeof editionQuery === 'string') {
            return editionQuery;
        }
        if (!editionQuery && isInitiaRender.current) {
            return initialCurrentEdition?.name;
        }
        if (!editionQuery && !isInitiaRender.current) {
            return 'Main';
        }
        return 'Main';
    }, [initialCurrentEdition?.name, router.query]);
    const { data: { currentEdition, editions } = {} } = useQuery(['artwork', id, currentEditionName], async () => {
        // const artwork = await getArtwork(id, artworkQuery)
        const artwork = await getCustomArtwork(id);
        const _editions = mapIArtworkToEditions(artwork?.data) || {};
        const _currentEdition = _editions.find(({ name }) => name === currentEditionName);
        if (!_currentEdition)
            return { currentEdition: null, editions: [] };
        const upcomingRelease = _currentEdition.artworkDetails.upcomingRelease;
        let upcomingReleaseUser = false;
        if (upcomingRelease && isAuthenticated) {
            upcomingReleaseUser = await isUpcomingReleaseUser();
        }
        setArtworkDetails({
            ..._currentEdition.artworkDetails,
            currentEditionName: _currentEdition.name,
            currentEditionOtherName: _currentEdition.otherName,
            upcomingRelease: upcomingRelease,
            upcomingReleaseUser: upcomingReleaseUser
        });
        return { currentEdition: _currentEdition, editions: _editions };
    }, {
        keepPreviousData: false,
        refetchOnMount: true,
        enabled: Boolean(id),
        initialData: {
            currentEdition: initialCurrentEdition,
            editions: initialEditions
        },
        onError() {
            if (initialCurrentEdition) {
                redirectToEdition(initialCurrentEdition.name);
            }
        }
    });
    const savedArtworkQueryKey = ['saved-artwork', id];
    const { data: savedArtworkId, refetch: refetchIsSaved } = useQuery(savedArtworkQueryKey, async () => {
        if (!id)
            return;
        const { error, id: savedArtworkId } = await isArtworkSaved(id);
        if (error)
            return null;
        return savedArtworkId;
    }, {
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        enabled: isAuthenticated,
        initialData: null
    });
    const { saveSearchedIds } = useLatestSearches<ArtworkCardProps>({
        computeIds: false,
        computeEntities: false,
        entities: 'artwork',
        key: 'ArtworkPage-searched-artworks'
    });
    const { mutate: mutateSaveArtwork, isLoading: isLoadingSaveMutation } = useMutation({
        mutationKey: ['save-artwork', id],
        mutationFn: async (currentSavedArtworkId: number | null | undefined) => {
            if (isLoadingSaveMutation)
                return;
            if (currentSavedArtworkId) {
                return unsaveArtwork(currentSavedArtworkId);
            }
            return saveArtwork(id);
        },
        async onMutate(currentSavedArtworkId) {
            if (isLoadingSaveMutation)
                return;
            await queryClient.cancelQueries({ queryKey: savedArtworkQueryKey });
            // if currentSavedArtworkId is provided, user is unfollowing, so we update the ui to undefined
            // otherwise, the user is following the artwork and we set the id to 1 (just a placeholder) because we don't know beforehand what id the following will have
            // the id will be updated when the query is refetched
            queryClient.setQueryData(savedArtworkQueryKey, currentSavedArtworkId ? null : 1);
            return { previousSavedArtworkId: currentSavedArtworkId };
        },
        onSuccess(data, currentSavedArtworkId) {
            if (!data)
                return;
            if (data.error) {
                throw new Error(data.error.message);
            }
            // if the mutation was called with no currentSavedArtworkId, it means that the user followed the artwork
            if (!currentSavedArtworkId) {
                setOverlayConfig({
                    className: 'flex items-center px-5',
                    body: <SavedArtworkModal src={firstImageUrl} alt={imageAlt}/>
                });
                setIsOverlayOpen(true);
            }
            refetchIsSaved();
        },
        onError(err, _, context) {
            // in case of error, revert the query data
            if (context?.previousSavedArtworkId !== undefined) {
                queryClient.setQueryData(savedArtworkQueryKey, context.previousSavedArtworkId);
            }
            console.error(err);
        }
    });
    const notifyMe = async () => {
        try {
            if (!user) {
                setAuthModalState('register');
                setAuthModalIsOpen(true);
                return;
            }
            setArtworkDetails({ ...artworkDetails, upcomingReleaseUser: true });
            const { success, details } = await notifyMeService(id);
            if (!success) {
                setArtworkDetails({ ...artworkDetails, upcomingReleaseUser: false });
                throw new Error(details);
            }
        }
        catch (err) {
            console.error(err);
        }
    };
    const isUpcomingReleaseUser = async () => {
        try {
            const { success, upcomingRelease, details } = await upcomingReleaseUserService(id);
            if (!success)
                throw new Error(details);
            return !!upcomingRelease;
        }
        catch (err) {
            console.error(err);
            return false;
        }
    };
    useEffect(() => {
        saveSearchedIds(id);
        if (id && name) {
            sendAnalyticsEvent({
                eventName: ANALYTICS_EVENTS.VIEW_ARTWORK,
                artworkId: String(id),
                artworkName: name
            });
            if (window?.klaviyo) {
                window?.klaviyo?.track('Viewed Product', {
                    ProductName: name,
                    ProductId: id
                });
            }
        }
        return () => {
            setIsDrawerOpen(false);
            setArtworkDetails(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);
    useAutomaticShowAuthModal({
        id,
        shouldDisplay: useCallback(() => {
            try {
                const viewedArtworks = Number(localStorage.getItem(VIEWED_ARTWORKS)) || 0;
                localStorage.setItem(VIEWED_ARTWORKS, JSON.stringify(viewedArtworks + 1));
                return viewedArtworks === 0 || (viewedArtworks + 1) % 3 === 0;
            }
            catch (err) {
                console.error(err);
                return false;
            }
        }, [])
    });
    const artistName = artists?.[0]?.name || '';
    const imageAlt = t('single_artwork:image_alt', {
        artistName,
        artworkName: name,
        releaseDate: currentEdition?.artworkDetails?.date,
        medium: currentEdition?.artworkDetails?.medium,
        material: currentEdition?.artworkDetails?.material,
        gallery: currentEdition?.artworkDetails?.gallery,
        features: Object.entries(currentEdition?.artworkDetails?.features ?? {})
            .map(([key, value]) => typeof value === 'boolean' && value ? titleCase(key) : null)
            .filter(Boolean)
            .join(', ')
    });
    const firstImage = images?.[0];
    const firstImageUrl = firstImage && isMediaImage(firstImage)
        ? (firstImage as Media).url || (firstImage as IMedia).attributes?.url
        : typeof firstImage === 'string'
            ? firstImage
            : undefined;
    const onSaveBtnClick = () => {
        if (!user) {
            setAuthModalState('register');
            setAuthModalIsOpen(true);
            return;
        }
        mutateSaveArtwork(savedArtworkId);
    };
    const redirectToEdition = useCallback((editionName: string) => {
        isInitiaRender.current = false;
        router.replace({
            pathname: router.asPath.split('?')[0],
            query: editionName !== 'Main' ? { edition: editionName } : {}
        }, undefined, { shallow: true });
    }, [router]);
    const onEditionChange = useCallback((edition: Edition) => {
        // setCurrentEditionName(edition.name)
        redirectToEdition(edition.name);
    }, [redirectToEdition]);
    const pageTitle = t('single_artwork:page_title', {
        artistName,
        artworkTitle: name
    });
    const pageDescription = t('single_artwork:page_desc', {
        artistName,
        artworkName: name,
        medium: currentEdition?.artworkDetails?.medium
    });
    return (<>
      <Head>
        <title>{pageTitle}</title>
        <meta name='description' content={pageDescription}/>
        <meta property='og:title' content={pageTitle}/>
        <meta property='og:description' content={pageDescription}/>
        {firstImageUrl && <meta property='og:image' content={firstImageUrl}/>}
        <link rel='canonical' href={`${process.env.NEXT_PUBLIC_SITE_URL}${getArtworkUrl(artists, name, id)}`}/>
      </Head>

      {currentEdition?.lowestAsk && (<script id='product-schema' type='application/ld+json' dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                    '@context': schemaOrgUrls.url,
                    '@type': 'WebPage',
                    primaryImageOfPage: firstImage && isMediaImage(firstImage)
                        ? getImageUrl(firstImage, 'largest')
                        : null,
                    mainEntity: {
                        '@type': 'Product',
                        name: name,
                        description: imageAlt,
                        image: firstImage && isMediaImage(firstImage)
                            ? getImageUrl(firstImage, 'largest')
                            : null,
                        releaseDate: currentEdition.artworkDetails.date,
                        model: currentEdition.artworkDetails.medium,
                        itemCondition: schemaOrgUrls.newCondition,
                        category: currentEdition.artworkDetails.medium,
                        url: `${process.env.NEXT_PUBLIC_SITE_URL}${getArtworkUrl(artists, name, id)}`,
                        brand: {
                            '@type': 'Brand',
                            name: artistName
                        },
                        depth: currentEdition.artworkDetails.size?.depth,
                        width: currentEdition.artworkDetails.size?.width,
                        height: currentEdition.artworkDetails.size?.height,
                        material: currentEdition.artworkDetails.material,
                        size: currentEdition.artworkDetails.size?.sizeType,
                        offers: {
                            '@type': 'Offer',
                            availability: schemaOrgUrls.inStock,
                            price: (currentEdition.lowestAsk.price || 0) / 100,
                            priceCurrency: currentEdition.lowestAsk.currency,
                            shippingDetails: shippingCost
                                ? {
                                    '@type': 'OfferShippingDetails',
                                    shippingRate: {
                                        '@type': 'MonetaryAmount',
                                        value: shippingCost / 100,
                                        currency: 'GBP'
                                    }
                                }
                                : undefined
                        }
                    }
                })
            }}/>)}

      {artistHasAuction && <AvailableAuctionBanner />}

      <div className='relative contents bg-cool-50 lg:bg-transparent'>
        <div className={classNames('container border-b border-cool-300 bg-cool-50 pb-10 lg:bg-transparent lg:pb-16')}>
          <Breadcrumbs url={url} isArtworkPath/>

          <div className='block grid-cols-6 grid-areas-artwork sm:px-6 md:grid-cols-12 md:px-0 md:grid-areas-artworkMd lg:grid lg:space-x-4 lg:space-y-0 lg:grid-areas-artworkLg'>
            {images?.length > 0 && (<div className='relative mb-5 w-full grid-in-artwork-gallery lg:row-span-3  lg:mb-0 lg:pr-12'>
                <div className='relative'>
                  <SlideShow className='h-full lg:h-auto lg:pt-0' images={images} alt={imageAlt}/>

                  {messageToShow && (<div className='absolute left-1/2 top-6 z-[1] hidden w-full -translate-x-1/2 text-center lg:flex lg:items-center lg:justify-center'>
                      <PopularityMessage messageToShow={messageToShow}>
                        {t(popularityTexts[messageToShow])}
                      </PopularityMessage>
                    </div>)}
                </div>

                <SaveButton onClick={onSaveBtnClick} hasFollowed={Boolean(savedArtworkId)}/>
              </div>)}

            {messageToShow && (<div className='mb-5 w-full text-center lg:hidden'>
                <PopularityMessage messageToShow={messageToShow}>
                  {t(popularityTexts[messageToShow])}
                </PopularityMessage>
              </div>)}

            <div className='contents lg:block lg:grid-in-artwork-header'>
              <ErrorBoundary>
                <header className={classNames('flex items-start justify-between', (!editions?.length || editions?.length <= 1) && 'lg:mb-6')}>
                  <div>
                    <ArtistsNames artists={artists}/>

                    <h1 className='text-2xl font-medium tracking-normal lg:text-3xl'>
                      {name}{' '}
                      {getEditionShortName(currentEditionName, currentEdition?.otherName)}
                    </h1>
                  </div>

                  <div className='aspect-1 h-full max-h-[3rem] min-h-[3rem] w-full max-w-[3rem] sm:max-h-[4rem] sm:min-h-[4rem] sm:min-w-[4rem] sm:max-w-[4rem] xl:max-h-[5rem] xl:min-h-[5rem] xl:min-w-[5rem] xl:max-w-[5rem]'>
                    {currentEdition?.artworkDetails?.royalty ? (<Badge type={currentEdition?.artworkDetails.royalty} className='flex h-full w-full'/>) : (<Skeleton className='h-full w-full' borderRadius={8}/>)}
                  </div>
                </header>
              </ErrorBoundary>

              <ErrorBoundary>
                {(!editions || editions.length > 1) && (<div className='-mx-1.5 mt-[10px] flex flex-wrap items-center justify-between gap-x-4 gap-y-1 lg:mb-6 lg:justify-start'>
                    {!editions && <Skeleton width={207} height={32}/>}

                    {editions && editions.length > 1 && (<div className='flex items-center space-x-3'>
                        {editions
                    .sort((a, b) => new Date(a.createdAt).valueOf() -
                    new Date(b.createdAt).valueOf())
                    .map((edition, index) => {
                    return (<button key={index} onClick={() => onEditionChange(edition)} className={classNames('flex items-center space-x-1 rounded-[20px] border border-cool-400 px-[10px] py-1 text-sm font-medium text-cool-700 duration-200 ease-in-out hover:border-cool-800 hover:text-cool-800', currentEditionName === edition?.name &&
                            'border-cool-800 text-cool-800')} data-e2e={`${edition.name.toLowerCase()}-edition-button`}>
                                <span>
                                  {getEditionShortName(edition?.name, edition.otherName, false) || (<>
                                      Main{' '}
                                      <span className='hidden lg:inline'>
                                        edition
                                      </span>
                                    </>)}
                                </span>

                                {Boolean(edition.asks.length) && (<Popover content={t('single_artwork:available')} className='!w-max' side='top'>
                                    <p className='sr-only'>
                                      {t('single_artwork:available')}
                                    </p>
                                    <span className='block h-2 w-2 rounded-full bg-emerald-800'></span>
                                  </Popover>)}
                              </button>);
                })}
                      </div>)}

                    {currentEditionName && currentEditionName === 'Main' && (<p className='w-max text-sm [@media(max-width:410px)]:hidden'>
                        <Trans i18nKey={t('single_artwork:how_buying_and_selling_work')} components={{
                    buying: (<button type='button' className='text-secondary-default underline' onClick={() => setWaysTo({
                            type: 'buy',
                            edition: currentEdition!,
                            artwork: { id, name },
                            artists
                        })}/>),
                    selling: (<button type='button' className='text-secondary-default underline' onClick={() => setWaysTo({
                            type: 'sell',
                            edition: currentEdition!,
                            artwork: { id, name },
                            artists
                        })}/>)
                }}/>
                      </p>)}

                    {currentEditionName && currentEditionName !== 'Main' && (<a href={routes.intercom.aappDifference} target='_blank' rel='noopener noreferrer' className='text-sm text-secondary-default'>
                        {currentEditionName.toLowerCase().startsWith('a') &&
                    t('single_artwork:what_is_an_ap')}
                        {currentEditionName.toLowerCase().startsWith('p') &&
                    t('single_artwork:what_is_a_pp')}
                      </a>)}
                  </div>)}
              </ErrorBoundary>

              <ErrorBoundary>
                <ExistingTrades currentEdition={currentEdition} artists={artists} name={name} id={id}/>
              </ErrorBoundary>

              <ErrorBoundary>
                {!artworkDetails?.upcomingRelease &&
            currentEdition?.name === currentEditionName && (<ArtworkTrade currentEdition={currentEdition} artists={artists} artwork={{ id, name }}>
                      {messageToShow === 'nearly-gone' && (<Popover content={t('single_artwork:nearly_gone_tooltip')} side='top'>
                          <PillMessage variant='yellow' size='small' className='mb-5 hidden !w-full text-center lg:block'>
                            {t(popularityTexts[messageToShow])}
                          </PillMessage>
                        </Popover>)}
                    </ArtworkTrade>)}

                {currentEdition?.name !== currentEditionName && (<ArtworkTradeSkeleton className='mt-6 lg:mt-0'/>)}
              </ErrorBoundary>

              {!artworkDetails?.upcomingRelease && (<ErrorBoundary>
                  <ViewPreviousTrades currentEdition={currentEdition} hasSales={Boolean(hasSales)} id={id}/>
                </ErrorBoundary>)}
              {artworkDetails?.upcomingRelease && (<ErrorBoundary>
                  <div className='item-center my-8 flex flex-col justify-center rounded bg-purple-50 px-6 py-3'>
                    <div className='flex justify-center text-sm font-medium lg:text-base'>
                      {t('single_artwork:available_soon')}
                    </div>

                    {artworkDetails?.upcomingReleaseUser ? (<>
                        <Button className='mb-3 mt-3 rounded-3xl font-medium lg:mx-auto lg:max-w-[10rem]' style='solid-primary'>
                          {t('single_artwork:subscribed')}
                          <Icon name='check' size={15} className='ml-3 text-cool-50'/>
                        </Button>
                        <div className='flex justify-center text-sm'>
                          {t('single_artwork:we_will_notify_you')}
                        </div>
                      </>) : (<Button className='mt-3 rounded-3xl font-medium lg:mx-auto lg:max-w-[10rem]' style='solid-primary' onClick={notifyMe}>
                        {t('single_artwork:notify_me')}
                        <Icon name='bell' size={15} className='ml-3 text-cool-50'/>
                      </Button>)}
                  </div>
                </ErrorBoundary>)}
            </div>

            <div className='mt-6 grid-in-artwork-body lg:min-h-[510px]'>
              <ErrorBoundary>
                <ArtworkTags isVerified isNewCondition currentEdition={currentEdition}/>
              </ErrorBoundary>

              <div className='flex flex-col lg:flex-col-reverse'>
                <div className={classNames('px-6 py-4', '-mx-6 my-5 w-[calc(100%+2.5rem)] bg-warm-100 text-center text-sm sm:-mx-12 sm:w-[calc(100%+6rem)] lg:mx-0 lg:mb-0 lg:mt-8 lg:flex lg:w-full lg:items-center lg:space-x-2 lg:border lg:border-cool-300 lg:bg-cool-50 lg:text-start')}>
                  <Icon name='filled-alert-question' filled className='hidden text-secondary-default lg:block' size={16}/>

                  <p>
                    <Trans i18nKey='single_artwork:have_a_question_chat_with_team' components={{
            link: (<IntercomTrigger id='chat-with-team-btn' data-e2e='chat-with-team-btn' className='text-secondary-default underline' defaultMessage={`Hi, I have a question about this artwork: ${process.env.NEXT_PUBLIC_SITE_URL}${getArtworkUrl(artists, name, id)}`}/>)
        }}/>
                  </p>
                </div>
                <ErrorBoundary>
                  <ArtworkDetails {...artworkDetails}/>
                </ErrorBoundary>
              </div>
            </div>
          </div>
        </div>

        {featureFlags?.reviews && (<OnScreenComponent fallback={<ReviewsCarouselSkeleton />}>
            <Suspense fallback={<ReviewsCarouselSkeleton />}>
              <ReviewsCarousel />
            </Suspense>
          </OnScreenComponent>)}

        {featureFlags?.similar_artworks && (<LooksSimilarCarousel artworkId={id}/>)}

        <MoreFromArtistCarousel artists={artists} id={id}/>

        <OnScreenComponent fallback={<SearchedArtworksContainer>
              <Skeletons type='artwork'/>
            </SearchedArtworksContainer>}>
          <SearchedArtworksCarousel className='bg-cool-50'/>
        </OnScreenComponent>
      </div>
    </>);
};
Artwork.getLayout = (page: ReactElement) => (<BaseLayout style='off-white-bg'>{page}</BaseLayout>);
const fetchData = async (id: number) => {
    const artworkData = await getArtwork(id, artworkQuery);
    const name = artworkData?.data?.attributes?.name || '';
    const images: TImages = artworkData?.data?.attributes?.images?.data
        ?.sort((img1, img2) => sortArtworkImages(img1, img2))
        ?.map(image => ({
        id: image?.id,
        url: image?.attributes?.url,
        formats: image?.attributes?.formats
    })) || [ARTWORK_IMG_PLACEHOLDER];
    const editions = mapIArtworkToEditions(artworkData?.data) || {};
    const artists = mapIArtistsToArtists(artworkData?.data?.attributes?.artists?.data) || [];
    return {
        name,
        artists,
        images,
        editions,
        spreadPercentage: artworkData.data.attributes.spreadPercentage
    };
};
const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
    const slug = params?.slug;
    if (!slug) {
        return { notFound: true, props: { error: 'NO SLUG' } };
    }
    const id = Number(slug[slug.length - 1]);
    if (isNaN(id)) {
        return { notFound: true, props: { error: 'INVALID ID' } };
    }
    try {
        const initialData = await fetchData(id);
        if (!initialData)
            return { notFound: true, props: { error: 'NO INITIAL DATA' } };
        const { editions = [], spreadPercentage } = initialData;
        const mainEdition = editions.find(e => e.name === 'Main') || null;
        const alternativeEditionLo = editions.find(e => e.name !== 'Main' && e.lowestAsk);
        const alternativeEditionHb = editions.find(e => e.name !== 'Main' && e.lowestAsk);
        const initialCurrentEdition = (() => {
            if (alternativeEditionLo &&
                mainEdition &&
                ((alternativeEditionLo.lowestAsk && !mainEdition.lowestAsk) ||
                    (alternativeEditionLo.lowestAsk?.priceGbp || 0) <
                        (mainEdition.lowestAsk?.priceGbp || 0)))
                return alternativeEditionLo;
            if (alternativeEditionHb &&
                mainEdition &&
                !mainEdition.lowestAsk &&
                ((alternativeEditionHb.highestBid && !mainEdition.highestBid) ||
                    (alternativeEditionHb.highestBid?.priceGbp || 0) >
                        (mainEdition.highestBid?.priceGbp || 0)))
                return alternativeEditionHb;
            return mainEdition;
        })();
        const { data: hasSales } = await getHasSales(id);
        const { data: wasSoldRecently } = await getWasSoldRecently(id);
        const { data: isTopViewed } = await getIsTopViewed(id);
        const isNearlyGone = _isNearlyGone(spreadPercentage);
        const messageToShow: MessageToShow | null = (() => {
            if (isNearlyGone)
                return 'nearly-gone';
            if (wasSoldRecently)
                return 'recently-sold';
            if (isTopViewed)
                return 'trending';
            return null;
        })();
        let shippingCost: number | null = null;
        try {
            if (initialCurrentEdition?.shipmentCategory &&
                initialCurrentEdition?.lowestAsk) {
                const res = await getShippingCost({
                    buyingPrice: initialCurrentEdition.lowestAsk?.priceGbp || 0 / 100,
                    shipmentCategory: initialCurrentEdition.shipmentCategory,
                    countryCode: 'GB',
                    framed: Boolean(initialCurrentEdition?.artworkDetails?.features?.framed),
                    galleryName: initialCurrentEdition.artworkDetails?.gallery,
                    shipmentWeightOverride: initialCurrentEdition.shipmentWeightOverride
                });
                if (typeof res === 'number')
                    shippingCost = res;
            }
        }
        catch (err) {
            /* empty */
        }
        return {
            props: {
                id,
                initialData,
                initialCurrentEdition,
                initialArtworkDetails: initialCurrentEdition
                    ? {
                        ...initialCurrentEdition.artworkDetails,
                        currentEditionName: initialCurrentEdition.name
                    }
                    : null,
                shippingCost,
                url: typeof slug !== 'string'
                    ? `${routes.artworks}/${slug.join('/')}`
                    : '',
                hasSales,
                wasSoldRecently,
                isTopViewed,
                isNearlyGone,
                messageToShow
            },
            revalidate: 12 * 60 * 60
        };
    }
    catch (err) {
        console.log(err);
        return { notFound: true, props: { error: (err as Error).message, err } };
    }
};
export const getStaticPaths: GetStaticPaths = async () => {
    const paths: GetStaticPathsResult['paths'] = [];
    try {
        // generate paths for the artworks on homepage
        const artworks = await Promise.all([
            fetchFeaturedArtworks(),
            fetchLatestArtworksAsks(),
            fetchLatestArtworksBids(),
            fetchNewReleases(),
            fetchByMedium('Print'),
            fetchByMedium('Sculpture'),
        ]);
        artworks.flat().forEach(artwork => {
            if (artwork.href) {
                paths.push({
                    params: {
                        slug: artwork.href
                            .replace(/\/\//g, '/')
                            .replace('/artworks/', '')
                            .split('/')
                    }
                });
            }
        });
    }
    catch (err) {
        console.error(err);
    }
    return { paths, fallback: 'blocking' };
};
export default Artwork;

    async function __Next_Translate__getStaticProps__19bb23d2e38__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/artworks/[...slug]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__19bb23d2e38__ as getStaticProps }
  